import { useState } from "react"

type BucketTone = "green" | "amber" | "muted" | "brand"

const TONE_STYLES: Record<
  BucketTone,
  { iconBg: string; iconColor: string; borderColor?: string; headBg?: string }
> = {
  green: {
    iconBg: "rgba(52,211,153,0.12)",
    iconColor: "var(--accent-green)",
  },
  amber: {
    iconBg: "rgba(251,191,36,0.12)",
    iconColor: "var(--accent-amber)",
    borderColor: "rgba(251,191,36,0.3)",
    headBg: "rgba(251,191,36,0.03)",
  },
  muted: {
    iconBg: "rgba(107,101,96,0.12)",
    iconColor: "var(--text-muted)",
  },
  brand: {
    iconBg: "rgba(245,158,11,0.12)",
    iconColor: "var(--accent-amber)",
  },
}

interface ReviewBucketProps {
  label: string
  sublabel?: string
  count: number
  tone: BucketTone
  icon: string
  defaultOpen?: boolean
  emptyText?: string
  children?: React.ReactNode
}

export function ReviewBucket({
  label,
  sublabel,
  count,
  tone,
  icon,
  defaultOpen = false,
  emptyText,
  children,
}: ReviewBucketProps) {
  const [open, setOpen] = useState(defaultOpen && count > 0)
  const styles = TONE_STYLES[tone]

  return (
    <div
      className="mb-2.5 overflow-hidden rounded-[12px] border border-border-subtle"
      style={styles.borderColor ? { borderColor: styles.borderColor } : undefined}
    >
      <button
        type="button"
        className="flex w-full items-center gap-2.5 px-3.5 py-3 text-left transition-colors hover:bg-bg-elevated/50"
        style={styles.headBg ? { background: styles.headBg } : undefined}
        onClick={() => setOpen((o) => !o)}
      >
        <div
          className="flex size-6 shrink-0 items-center justify-center rounded-[6px] text-[12px]"
          style={{ background: styles.iconBg, color: styles.iconColor }}
        >
          {icon}
        </div>
        <div className="min-w-0 flex-1">
          <span className="text-[13px] font-semibold text-text-primary">{label}</span>
          {sublabel && (
            <span className="ml-1.5 text-[12.5px] font-normal text-text-muted">{sublabel}</span>
          )}
        </div>
        <span className="font-mono text-[11px] text-text-muted mr-1.5">{count}</span>
        <span
          className="text-[11px] text-text-muted"
          style={{ transform: open ? "none" : "rotate(-90deg)", display: "inline-block" }}
        >
          ▾
        </span>
      </button>

      {open && (
        <div className="border-t border-border-subtle bg-bg-surface px-3.5 pb-2 pt-1">
          {count === 0 && emptyText ? (
            <p className="py-2 text-[12px] text-text-muted">{emptyText}</p>
          ) : (
            children
          )}
        </div>
      )}
    </div>
  )
}
